import { useLayoutEffect, useRef, useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import CareerApplicationForm from '@/components/CareerApplicationForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Heart, TrendingUp, Gift, CheckCircle2, Briefcase } from 'lucide-react';
import { PublicJobsList } from '@/components/careers/PublicJobsList';

// Consolidated /careers page (was previously split between /careers
// narrative + /careers/jobs listings). Live requisitions are surfaced
// from the database via <PublicJobsList /> and grouped by posting type
// (Open / Pending Award / Forecasted / Always Recruiting). The narrative
// intro, disciplines, and benefits sections remain to communicate brand
// and culture before the live listings.

const CareersPage = () => {
  const [showGeneralForm, setShowGeneralForm] = useState(false);
  const generalApplicationButton = useRef<HTMLButtonElement>(null);
  const applicationHeading = useRef<HTMLHeadingElement>(null);
  const returnScroll = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (showGeneralForm) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      applicationHeading.current?.focus({ preventScroll: true });
    } else if (returnScroll.current !== null) {
      window.scrollTo({ top: returnScroll.current, behavior: 'instant' });
      returnScroll.current = null;
      generalApplicationButton.current?.focus({ preventScroll: true });
      generalApplicationButton.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }, [showGeneralForm]);

  const disciplines = [
    'Machine Learning & Deep Learning',
    'Natural Language Processing',
    'Computer Vision',
    'Generative AI & LLMs',
    'Agentic & Multi-Agent Systems',
    'MLOps & Data Engineering',
    'Software Engineering',
    'Enterprise IT & Cloud',
    'Information Security',
    'Edge AI & IoT',
    'Program Management',
    'Strategy Consulting',
    'Supply Chain & Logistics',
  ];

  const practiceAreas = [
    {
      title: 'Applied AI & Data',
      description: 'Machine learning, generative AI, data engineering, and MLOps applied to real enterprise problems.',
    },
    {
      title: 'Engineering & Infrastructure',
      description: 'Software, enterprise IT, cloud, and information security built as durable, high-performance systems.',
    },
    {
      title: 'Programs & Advisory',
      description: 'Program management, strategy consulting, and supply chain work that keeps engagements moving.',
    },
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Benefits',
      description: 'Comprehensive health, dental, vision, and retirement planning paired with a competitive total rewards package.',
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Hands-on access to modern AI tooling, conferences, and continuous learning to grow your craft.',
    },
    {
      icon: Users,
      title: 'Excellence',
      description: 'Collaborate with experienced engineers and program leaders committed to high-quality outcomes.',
    },
    {
      icon: Gift,
      title: 'Flexibility',
      description: 'Remote-friendly culture with adaptable schedules that support focused, productive work.',
    },
  ];

  // General application form view
  if (showGeneralForm) {
    const formPosition = {
      id: 'general-application',
      title: 'General Application',
      type: 'remote' as const,
      description: 'Submit a general application to be considered for current and future opportunities.',
      tags: [],
    };

    return (
      <div className="min-h-dvh bg-background">
        <SEO
          title="Apply - General Application"
          description="Submit a general application to AKIVA."
          canonicalUrl="/careers"
        />
        <Navigation />
        <main id="main-content" tabIndex={-1} className="pt-24 pb-16 bg-gradient-to-b from-muted/30 to-background dark:from-muted/10 dark:to-background">
          <div className="container-max max-w-3xl">
            <CareerApplicationForm
              position={formPosition}
              headingRef={applicationHeading}
              backLabel="Back to careers"
              onBack={() => setShowGeneralForm(false)}
            />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <SEO
        title="Careers"
        description="Join AKIVA. We hire engineers, data and IT specialists, program managers, and consultants across commercial and government engagements."
        keywords="engineering careers, AI engineer jobs, data engineer jobs, program management jobs, IT careers, government contracting jobs"
        canonicalUrl="/careers"
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-muted/30 to-background dark:from-muted/10 dark:to-background">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              <span className="block text-muted-foreground">Push the boundaries</span>
              <span className="block text-primary">of modern engineering.</span>
            </h1>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Join engineers, data specialists, IT professionals, and program leaders solving hard problems across commercial and government engagements, from applied AI to enterprise infrastructure.
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="btn-hero" asChild>
                <a href="#open-positions">View Open Positions</a>
              </Button>
              <Button size="lg" variant="outline" asChild className="btn-outline-hero">
                <a href="/ai-network">Join AI Innovation Network</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Live Open Positions — grouped by posting type, pulled from
          the requisitions table. Replaces the previous hard-coded
          "Roles We Hire For" cards so this stays a single source of
          truth with the admin Requisitions module. */}
      <section className="section-padding bg-muted/30" id="open-positions">
        <div className="container-max max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary mb-4">Open Positions</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Current openings, contract-contingent roles, anticipated needs, and disciplines we recruit across continuously.
            </p>
          </div>

          <PublicJobsList />

          {/* General Application CTA */}
          <div className="text-center pt-12 mt-12 border-t border-border max-w-4xl mx-auto">
            <p className="text-muted-foreground mb-4">
              Don't see a perfect fit? We're always interested in exceptional people.
            </p>
            <Button
              ref={generalApplicationButton}
              size="lg"
              variant="outline"
              onClick={() => {
                returnScroll.current = window.scrollY;
                setShowGeneralForm(true);
              }}
            >
              <Briefcase className="h-4 w-4 mr-2" />
              Submit General Application
            </Button>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="section-padding bg-background">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary mb-4">Where You'll Work</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Engineering, infrastructure, and advisory work across our service pillars, from strategy through deployment
            </p>
          </div>

          {/* Disciplines we hire across */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Disciplines We Hire Across</h3>
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8">
              <div className="flex flex-wrap gap-3 justify-center">
                {disciplines.map((discipline) => (
                  <span
                    key={discipline}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium hover:bg-primary/20 transition-colors"
                  >
                    {discipline}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          {/* Practice areas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceAreas.map((pillar) => (
              <Card
                key={pillar.title}
                className="border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-300 text-center flex flex-col h-full"
              >
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-foreground">{pillar.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold mb-6 text-primary">
                  Why Choose AKIVA
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  AKIVA is a place for engineers, specialists, and program leaders who want to do meaningful work across AI, enterprise services, and infrastructure. We focus on practical innovation, durable systems, and outcomes our clients can measure.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <p className="text-muted-foreground"><strong className="text-foreground">Meaningful Work:</strong> Build applied AI, enterprise software, and infrastructure that solve real problems.</p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <p className="text-muted-foreground"><strong className="text-foreground">Engineering Discipline:</strong> Ship reliable, well-architected solutions with the support of experienced teammates.</p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <p className="text-muted-foreground"><strong className="text-foreground">Total Rewards:</strong> Competitive compensation, comprehensive benefits, and access to modern tooling.</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                  <h3 className="font-bold text-foreground mb-2 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-primary" />
                    Remote Work Policy
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We offer flexible hybrid and remote work arrangements. Most positions can be performed remotely with periodic travel for client meetings and team collaboration sessions. Flexible schedules designed for optimal productivity and innovation.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card
                  key={benefit.title}
                  className="border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-300 fade-in-up flex flex-col h-full"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardHeader>
                    <div className="w-10 h-10 rounded-md bg-primary/10 dark:bg-primary/15 flex items-center justify-center mb-3">
                      <benefit.icon className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
